<script setup>
import {ref, computed, watch, onMounted} from 'vue'
import {useStore} from 'vuex'
import {useRoute} from 'vue-router'
import axios from 'axios'
import {Plus, Trash2, X, FolderDot, Undo2, Eye, FileUp} from 'lucide-vue-next'
import {debounce} from 'lodash'
import { bbox, area, centroid, length } from '@turf/turf'

import {Input} from '@/components/ui/input'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {FormFeature} from '@/components/forms'
import {Point, LineString, Polygon} from '@/components/icons'
import {Spinner} from '@/components/ui/spinner'
import {MapFileInput} from '@/components/map'
import {Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'

const route = useRoute()
const projectId = computed(() => parseInt(route.params.project_id))

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const draw = computed(() => store.getters['geo/getDraw'])
const allTemplates = computed(() => store.getters['geo/getAllTemplates'])
const projects = computed(() => store.getters['project/getProjects'])
const selectedProjectFilter = ref('all')
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])

const activeAction = ref(null)

const isOpen = ref(false)
const selectedGeometry = ref(null)
const isSearching = ref(false)
const searchText = ref('')
const searchFeatures = ref([])
const drawedData = ref([])
const features = computed(() => [...searchFeatures.value, ...drawedData.value])

const filteredTemplates = computed(() => {
  if (!allTemplates.value) return []

  if (selectedProjectFilter.value === 'all') {
    return allTemplates.value
  }

  return allTemplates.value.filter(t => t.project_id === parseInt(selectedProjectFilter.value))
})

const templatesByProject = computed(() => {
  if (!filteredTemplates.value) return {}

  const grouped = {}
  filteredTemplates.value.forEach(template => {
    const projectName = template.project_name || 'Unknown Project'
    if (!grouped[projectName]) {
      grouped[projectName] = []
    }
    grouped[projectName].push(template)
  })

  return grouped
})

const templateActions = computed(() => {
  if (!filteredTemplates.value) {
    return []
  }
  return filteredTemplates.value.map((template) => {
    return {
      name: template.name,
      type: template.geometry_type === 'Point' ? 'draw_point' : template.geometry_type === 'LineString' ? 'draw_line_string' : 'draw_polygon',
      color: template.color,
      s3_icon: template.s3_icon,
      template_id: template.id,
      project_name: template.project_name
    }
  })
})

const defaultGeometryActions = [
  {name: 'Point', type: 'draw_point', color: '0059ff', icon: 'point'},
  {name: 'Line', type: 'draw_line_string', color: '0059ff', icon: 'line-string'},
  {name: 'Polygon', type: 'draw_polygon', color: '0059ff', icon: 'polygon'},
]

// No need for combined actions since we handle them separately in the template

const toggle = () => {
  isOpen.value = !isOpen.value


  if (isOpen.value && selectedOrg.value) {
    store.dispatch('geo/fetchAllTemplates', selectedOrg.value.organization_id)
  }
}

const selectGeometry = (feature) => {
  if (!feature) {
    selectedGeometry.value = null
    return
  }
  selectedGeometry.value = feature
}

const onDrawUpdate = async (e) => {
  const data = draw.value.getAll()
  drawedData.value = []

  for (const [index, feature] of data.features.entries()) {
    let areaText = ''

    if (feature.geometry.type === 'Polygon') {
      const areaInSm = area(feature)
      const areaInHa = (areaInSm / 10000).toFixed(3) // 1 ha = 10000 sm
      const areaInSkm = (areaInSm / 1000000).toFixed(4) // 1 skm = 1000000 sm
      areaText = `${areaInHa} ha (${areaInSkm} km²)`

      feature.properties = {
        ...feature.properties,
        areaInSm: areaInSm,
        areaInHa: areaInHa,
        areaInSkm: areaInSkm,
        areaText: areaText
      }
    }

    if (feature.geometry.type === 'LineString') {
      const lengthInM = length(feature, { units: 'meters' })
      const lengthInKm = (lengthInM / 1000).toFixed(2)
      areaText = `${lengthInKm} km`

      feature.properties = {
        ...feature.properties,
        lengthInM: lengthInM,
        lengthInKm: lengthInKm,
        areaText: areaText
      }
    }

    const areaCenter = centroid(feature)
    const sourceId = 'areaLabelSource-' + feature.id
    const layerId = 'areaLabel-' + feature.id

    // Add color from activeAction if using a template
    // Ensure color always has # prefix
    let color = '#0059ff' // default color
    if (activeAction.value?.color) {
      // Handle both formats (with and without #)
      color = activeAction.value.color.startsWith('#') ? 
        activeAction.value.color : 
        `#${activeAction.value.color}`
    }
    
    feature.properties = {
      ...feature.properties,
      areaCenter: areaCenter,
      color: color
    }

    drawedData.value.push(feature)

    if (map.value.getSource(sourceId)) {
      map.value.getSource(sourceId).setData(areaCenter)
      map.value.setLayoutProperty(layerId, 'text-field', areaText)
    } else {
      map.value.addSource(sourceId, {
        type: 'geojson',
        data: areaCenter
      })

      map.value.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': areaText,
          'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
          'text-radial-offset': 0.5,
          'text-justify': 'auto',
          'text-size': 13
        }
      })
    }
  }
}

const focusOnFeature = (feature) => {
  const bounds = bbox(feature)
  map.value.fitBounds(bounds, { padding: 50, duration: 600 })
}

const showFeature = (feature) => {
  if (feature.properties.isSearch) {
    previewFeature(feature)
  } else {
    focusOnFeature(feature)
  }
}

const deleteFeature = (feature) => {
  if (!feature.id) return

  draw.value.delete(feature.id)
  cleanUpFeature(feature)
  drawedData.value = drawedData.value.filter((item) => item.id !== feature.id)
}

const previewFeature = (feature) => {
}

const resetSearch = () => {
  searchFeatures.value = []
  previewFeature(null)
  selectedGeometry.value = null
}

const reset = () => {
  activeAction.value = null
  searchText.value = ''
  draw.value.deleteAll()
  drawedData.value = []
  resetSearch()
}

const startDraw = (action) => {
  reset()
  activeAction.value = action
  draw.value.changeMode(action.type)
}

const searchLocation = async (value) => {
  resetSearch()
  try {
    const response = await axios.get('http://nominatim.openstreetmap.org/search', {
      params: {
        q: value,
        polygon_geojson: 1,
        format: 'json',
        limit: 6
      }
    })

    const results = response.data

    if (results.length === 0) {
      return
    }

    for (const result of results) {
      const feature = {
        type: 'Feature',
        geometry: result.geojson,
        properties: {
          isSearch: true,
          name: result.display_name || result.name,
          addresstype: result.addresstype,
        }
      }

      searchFeatures.value.push(feature)
    }
  } catch (error) {
  }
}

const search = debounce(async () => {
  isSearching.value = true
  await searchLocation(searchText.value)
  isSearching.value = false
}, 1000)

const handleFeatureUpdate = (updatedFeature) => {
  // Update the selected geometry with new properties (like color)
  if (selectedGeometry.value && updatedFeature) {
    selectedGeometry.value = updatedFeature

    // Find the draw feature that needs updating
    const drawFeatures = draw.value.getAll()
    const drawFeature = drawFeatures.features.find(f =>
      f.properties?.local_uuid === updatedFeature.properties?.local_uuid ||
      f.id === selectedGeometry.value.id
    )

    if (drawFeature) {
      // Store the feature ID for re-adding
      const featureId = drawFeature.id

      // Update properties including color
      drawFeature.properties = {
        ...drawFeature.properties,
        ...updatedFeature.properties
      }

      // Delete and re-add the feature to force style update
      draw.value.delete(featureId)

      // Re-add with the same ID to maintain consistency
      const updatedDrawFeature = {
        ...drawFeature,
        id: featureId,
        properties: {
          ...drawFeature.properties,
          color: updatedFeature.properties?.color || '#0059ff'
        }
      }

      draw.value.add(updatedDrawFeature)

      // Update drawedData to maintain consistency
      const dataIndex = drawedData.value.findIndex(item => item.id === featureId)
      if (dataIndex !== -1) {
        drawedData.value[dataIndex] = {
          ...drawedData.value[dataIndex],
          properties: updatedDrawFeature.properties
        }
      }
    }
  }
}

const close = () => {
  isOpen.value = false
  reset()
}

const cleanUpFeature = async (feature) => {
  const sourceId = 'areaLabelSource-' + feature.id
  const layerId = 'areaLabel-' + feature.id
  if (map.value.getSource(sourceId)) {
    map.value.removeLayer(layerId)
    map.value.removeSource(sourceId)
  }
}

const onDrawDelete = async (e) => {
  for (const feature of e.features) {
    cleanUpFeature(feature)
  }
}

onMounted(() => {
  // Fetch templates on mount in case dialog is already open
  if (isOpen.value && selectedOrg.value) {
    store.dispatch('geo/fetchAllTemplates', selectedOrg.value.organization_id)
  }

  map.value.on('draw.create', onDrawUpdate)
  map.value.on('draw.update', onDrawUpdate)
  map.value.on('draw.delete', onDrawDelete)
})

// No need to watch selectedProjectFilter - filtering happens in computed property

watch(() => features.value, (value) => {
  if (value.length === 0) {
    selectGeometry(null)
  }

  if (value.length > 0 && selectedGeometry.value === null) {
    selectGeometry(value[0])
  }
})

watch(() => searchText.value, async (value) => {
  if (searchText.value.length === 0) {
    resetSearch()
    return
  }

  await search()
})
</script>

<template>
  <div>
    <Transition name="slideToolbox">
      <div v-if="isOpen" class="bg-white rounded-lg shadow-lg max-h-[70vh] overflow-y-auto mb-2 -mr-11 lg:mr-0">
        <div v-if="allTemplates && allTemplates.length > 0" class="px-2 py-2 border-b border-gray-200">
          <Select v-model="selectedProjectFilter">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Filter templates by project" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Projects</SelectItem>
                <SelectItem
                  v-for="project in projects"
                  :key="project.project_id"
                  :value="project.project_id.toString()"
                >
                  {{ project.project_name }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div class="border-b border-gray-200 last:border-0">
          <div class="flex flex-wrap justify-start max-w-[285px] min-w-[285px] overflow-hidden">
            <!-- Always show File upload first -->
            <div class="basis-1/5 w-9 flex flex-col items-center my-2">
              <MapFileInput @change="() => close()" />
            </div>
            
            <!-- Always show default geometry actions -->
            <div v-for="action of defaultGeometryActions" :key="action.name" class="basis-1/5 w-9 flex flex-col items-center my-2">
              <Button
                class="w-9 h-9 rounded-full p-0 relative"
                :class="{'outline outline-2 outline-black': activeAction && action.name === activeAction.name}"
                :style="{backgroundColor: '#' + action.color}"
                :aria-label="`Draw ${action.name} on map`"
                :aria-pressed="activeAction && action.name === activeAction.name"
                :title="`Draw ${action.name} on map`"
                :data-testid="`draw-${action.type}-button`"
                :id="`draw-${action.name.toLowerCase()}-button`"
                type="button"
                role="button"
                @click="startDraw(action)"
              >
                <Plus
                  class="w-3 h-3 absolute top-0 left-0 bg-secondary border rounded-full"
                  :style="{color: '#' + action.color, borderColor: '#' + action.color}"
                  aria-hidden="true"
                />
                <Point class="fill-white" v-if="action.icon === 'point'" aria-hidden="true" />
                <LineString class="fill-white" v-else-if="action.icon === 'line-string'" aria-hidden="true" />
                <Polygon class="fill-white" v-else-if="action.icon === 'polygon'" aria-hidden="true" />
              </Button>
              <Label
                class="text-center mt-1 text-xs"
                style="line-height: 1;"
                :class="{'font-bold': activeAction && action.name === activeAction.name}"
                :for="`draw-${action.name.toLowerCase()}-button`"
              >
                {{ action.name }}
              </Label>
            </div>
            
            <!-- Show templates grouped by project when "All Projects" is selected -->
            <template v-if="selectedProjectFilter === 'all' && Object.keys(templatesByProject).length > 0">
              <template v-for="(templates, projectName) in templatesByProject" :key="projectName">
                <div class="w-full px-2 mt-2 mb-1">
                  <span class="text-xs font-semibold text-gray-600">{{ projectName }}</span>
                </div>
                <div v-for="template of templates" class="basis-1/5 w-9 flex flex-col items-center my-2">
                  <Button
                    class="w-9 h-9 rounded-full p-0 relative"
                    :class="{'outline outline-2 outline-black': activeAction && template.name === activeAction?.name}"
                    :style="{backgroundColor: template.color}"
                    :aria-label="`Draw ${template.name} on map`"
                    :aria-pressed="activeAction && template.name === activeAction?.name"
                    :title="`Draw ${template.name} on map`"
                    :data-testid="`draw-template-${template.id}-button`"
                    :id="`draw-template-${template.id}-button`"
                    type="button"
                    role="button"
                    @click="startDraw({
                      name: template.name,
                      type: template.geometry_type === 'Point' ? 'draw_point' : template.geometry_type === 'LineString' ? 'draw_line_string' : 'draw_polygon',
                      color: template.color,
                      s3_icon: template.s3_icon,
                      template_id: template.id,
                      project_id: template.project_id,
                      project_name: template.project_name
                    })"
                  >
                    <Plus
                      class="w-3 h-3 absolute top-0 left-0 bg-secondary border rounded-full"
                      :style="{color: template.color, borderColor: template.color}"
                      aria-hidden="true"
                    />
                    <div v-if="template.s3_icon" class="w-9 h-9 flex items-center justify-center">
                      <img class="object-contain w-5 h-5" :src="template.s3_icon" :alt="`${template.name} icon`">
                    </div>
                    <Point v-else-if="template.geometry_type === 'Point'" class="fill-white" aria-hidden="true" />
                    <LineString v-else-if="template.geometry_type === 'LineString'" class="fill-white" aria-hidden="true" />
                    <Polygon v-else-if="template.geometry_type === 'Polygon'" class="fill-white" aria-hidden="true" />
                  </Button>
                  <Label
                    class="text-center mt-1 text-xs"
                    style="line-height: 1;"
                    :class="{'font-bold': activeAction && template.name === activeAction?.name}"
                    :for="`draw-template-${template.id}-button`"
                  >
                    {{ template.name }}
                  </Label>
                </div>
              </template>
            </template>
            
            <!-- Show filtered templates when a specific project is selected -->
            <template v-else-if="selectedProjectFilter !== 'all' && filteredTemplates.length > 0">
              <div v-for="template of filteredTemplates" :key="template.id" class="basis-1/5 w-9 flex flex-col items-center my-2">
                <Button
                  class="w-9 h-9 rounded-full p-0 relative"
                  :class="{'outline outline-2 outline-black': activeAction && template.name === activeAction?.name}"
                  :style="{backgroundColor: template.color}"
                  :aria-label="`Draw ${template.name} on map`"
                  :aria-pressed="activeAction && template.name === activeAction?.name"
                  :title="`Draw ${template.name} on map`"
                  :data-testid="`draw-template-${template.id}-button`"
                  :id="`draw-template-${template.id}-button`"
                  type="button"
                  role="button"
                  @click="startDraw({
                    name: template.name,
                    type: template.geometry_type === 'Point' ? 'draw_point' : template.geometry_type === 'LineString' ? 'draw_line_string' : 'draw_polygon',
                    color: template.color,
                    s3_icon: template.s3_icon,
                    template_id: template.id,
                    project_id: template.project_id,
                    project_name: template.project_name
                  })"
                >
                  <Plus
                    class="w-3 h-3 absolute top-0 left-0 bg-secondary border rounded-full"
                    :style="{color: template.color, borderColor: template.color}"
                    aria-hidden="true"
                  />
                  <div v-if="template.s3_icon" class="w-9 h-9 flex items-center justify-center">
                    <img class="object-contain w-5 h-5" :src="template.s3_icon" :alt="`${template.name} icon`">
                  </div>
                  <Point v-else-if="template.geometry_type === 'Point'" class="fill-white" aria-hidden="true" />
                  <LineString v-else-if="template.geometry_type === 'LineString'" class="fill-white" aria-hidden="true" />
                  <Polygon v-else-if="template.geometry_type === 'Polygon'" class="fill-white" aria-hidden="true" />
                </Button>
                <Label
                  class="text-center mt-1 text-xs"
                  style="line-height: 1;"
                  :class="{'font-bold': activeAction && template.name === activeAction?.name}"
                  :for="`draw-template-${template.id}-button`"
                >
                  {{ template.name }}
                </Label>
              </div>
            </template>

            <div v-if="activeAction" class="basis-1/5 w-9 flex flex-col items-center my-2">
              <Button
                class="w-9 h-9 rounded-full p-0 relative bg-gray-100 text-black hover:bg-gray-200"
                aria-label="Reset drawing tools and clear selection"
                title="Reset drawing tools and clear selection"
                data-testid="reset-draw-button"
                id="reset-draw-button"
                type="button"
                role="button"
                @click="reset()"
              >
                <Undo2 class="w-4 h-4" aria-hidden="true" />
              </Button>
              <Label
                class="text-center text-xs"
                for="reset-draw-button"
              >
                Reset
              </Label>
            </div>
          </div>
        </div>

        <div v-if="activeAction" class="relative px-2 py-2 min-w-[300px]">
          <Input
            placeholder="Search for country, city or place"
            v-model="searchText"
            aria-label="Search for location"
            data-testid="location-search-input"
          />
          <Button
            v-if="searchText.length > 0 && !isSearching"
            variant="ghost"
            class="p-0 w-6 h-6 absolute top-4 right-4"
            @click="searchText = ''"
            aria-label="Clear search"
            title="Clear search"
            data-testid="clear-search-button"
          >
            <X class="w-4 h-4 text-gray-500" />
          </Button>
          <Spinner v-if="isSearching" class="w-4 h-4 absolute top-3.5 right-2" />
        </div>

        <div v-if="activeAction && features.length > 0" class="px-2 pb-2">
          <div class="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div
              v-for="(feature, index) in features"
              :key="feature.id"
              class=" border-gray-200 border-b last:border-b-0 cursor-pointer"
              :class="{'bg-secondary': selectedGeometry === feature}"
            >
              <div class="flex justify-between">
                <div @click="selectGeometry(feature)" class="w-full px-2 py-1">
                  <div class="flex items-center justify-between">
                    <h4 class="text-sm text-gray-600 mb-0.5" :class="{'font-semibold' : selectedGeometry === feature}">
                      <span v-if="!feature.properties.name">Drawed ({{ index + 1 }})</span>
                      <span v-else>{{ feature.properties.name }}</span>
                    </h4>
                  </div>
                  <ul class=" text-gray-400 text-xs">
                    <li v-if="feature.properties.addresstype" class="capitalize">Type: {{ feature.properties.addresstype }}</li>
                    <li>Geometry: {{ feature.geometry.type }}</li>
                    <li v-if="feature.properties.areaText">Size: {{ feature.properties.areaText }}</li>
                  </ul>
                </div>
                <div class="flex p-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    class="w-6 h-6 p-1"
                    @click="showFeature(feature)"
                    aria-label="Show feature on map"
                    title="Show feature on map"
                    data-testid="show-feature-button"
                  >
                    <Eye class="text-gray-600" />
                  </Button>
                  <Button
                    v-if="!feature.properties.isSearch"
                    @click="deleteFeature(feature)"
                    size="sm"
                    variant="ghost"
                    class="w-6 h-6 p-1"
                    aria-label="Delete feature"
                    title="Delete feature"
                    data-testid="delete-feature-button"
                  >
                    <Trash2 class="text-red-400" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="activeAction && features.length === 0" class="pb-2">
          <p class="text-gray-500 text-center py-2">Start drawing or search</p>
        </div>

        <div v-if="activeAction && features.length > 0" class="p-2 border-t border-gray-200">
          <div>
            <FormFeature
              v-if="selectedGeometry"
              :feature="selectedGeometry"
              :template-id="activeAction?.template_id ? activeAction.template_id.toString() : null"
              :template-project-id="activeAction?.project_id ? activeAction.project_id.toString() : null"
              @update:created="close()"
            />
          </div>
        </div>
      </div>
    </Transition>
    <div class="flex items-center justify-end">
      <Button
        @click="toggle"
        class="rounded-full shadow-md h-14 w-14 p-0 bg-primary hover:bg-primary/90 text-white"
        aria-label="Create new data feature"
        title="Create new data feature"
        data-testid="create-data-button"
        id="create-data-button"
        :aria-expanded="isOpen"
        aria-haspopup="true"
        type="button"
        role="button"
      >
        <Plus
          :class="{'rotate-45': isOpen}"
          class="w-8 h-8 transition duration-150 ease-out"
          aria-hidden="true"
        />
        <span class="sr-only">
          {{ isOpen ? 'Close' : 'Open' }} drawing tools
        </span>
      </Button>
    </div>
  </div>
</template>

<style>
.slideToolbox-enter-active, .slideToolbox-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.slideToolbox-enter-from, .slideToolbox-leave-to {
  transform: translateY(10%);
  opacity: 0;
}
</style>
